const ISO_COUNTRY_CODES = "AD AE AF AG AI AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");

const countryDisplayNames = typeof Intl !== "undefined" && Intl.DisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

const priorityCountries = [
  { code: "XK", name: "Kosovo" },
  { code: "AL", name: "Albania" },
];

export const COUNTRY_OPTIONS = [
  ...priorityCountries,
  ...ISO_COUNTRY_CODES
    .filter((code) => !priorityCountries.some((country) => country.code === code))
    .map((code) => ({ code, name: countryDisplayNames?.of(code) || code }))
    .sort((left, right) => left.name.localeCompare(right.name)),
];

export const CUSTOMER_TYPES = [
  { value: "business", label: "Registered business" },
  { value: "consumer", label: "Individual / consumer" },
  { value: "unknown", label: "Not confirmed yet" },
];

export const SERVICE_CATEGORIES = [
  { value: "professional-services", label: "Professional or software services" },
  { value: "digital-services", label: "Automated digital services" },
  { value: "goods", label: "Goods or physical products" },
  { value: "property-rent", label: "Property or rent" },
  { value: "other", label: "Other / review manually" },
];

export const TAX_TREATMENTS = [
  { value: "added", label: "Add to invoice total" },
  { value: "included", label: "Already included in prices" },
  { value: "withheld", label: "Deduct / withhold from amount due" },
];

export const countryName = (code) => COUNTRY_OPTIONS.find((country) => country.code === code)?.name || "Not set";

export const createTaxLine = (overrides = {}) => ({
  id: overrides.id || `tax-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
  label: overrides.label || "Tax",
  rate: String(overrides.rate ?? ""),
  treatment: overrides.treatment || "added",
  enabled: overrides.enabled !== false,
});

export const cloneTaxLines = (lines = []) => lines.map((line, index) => createTaxLine({
  ...line,
  id: line.id || `tax-${index + 1}`,
}));

export const emptyTaxProfile = () => ({
  useAsDefault: false,
  confirmed: false,
  source: "unconfigured",
  ruleId: "",
  lines: [],
  invoiceNote: "",
  explanation: "",
});

export const normalizeTaxProfile = (profile) => ({
  ...emptyTaxProfile(),
  ...(profile || {}),
  lines: cloneTaxLines(profile?.lines || []),
});

export const getClientTaxProfile = (client, issuerId) => normalizeTaxProfile(
  client?.taxProfilesByIssuer?.[issuerId]
);

const suggestion = ({ id, title, summary, lines = [], invoiceNote = "", actionLabel = "Use as default", tone = "suggested", evidence = "" }) => ({
  id,
  title,
  summary,
  lines: cloneTaxLines(lines),
  invoiceNote,
  actionLabel,
  tone,
  evidence,
  requiresConfirmation: true,
});

export const getTaxSuggestion = ({ issuer, client }) => {
  if (!issuer?.countryCode || !client?.countryCode) {
    return suggestion({
      id: "missing-country",
      title: "Add both countries first",
      summary: "The issuer and client countries are needed before Invoice Studio can suggest a tax starting point.",
      actionLabel: "Country details required",
      tone: "incomplete",
    });
  }

  if (issuer.countryCode !== "XK") {
    return suggestion({
      id: "jurisdiction-not-verified",
      title: `${countryName(issuer.countryCode)} rules need review`,
      summary: "This jurisdiction pack has not been verified yet. Configure the invoice-visible taxes manually and confirm them with an accountant.",
      actionLabel: "Start a custom rule",
      tone: "review",
    });
  }

  if (issuer.vatStatus === "not-registered") {
    return suggestion({
      id: "xk-not-registered",
      title: "No VAT suggested from this issuer profile",
      summary: "The issuer is marked as not VAT registered. Keep VAT off by default, but review whether registration or another invoice treatment is required.",
      actionLabel: "Use issuer no-VAT default",
      tone: "review",
    });
  }

  if (issuer.vatStatus !== "registered") {
    return suggestion({
      id: "xk-registration-unknown",
      title: "Confirm the issuer's VAT status",
      summary: "Kosovo rates should only be suggested after the issuing business records whether it is VAT registered.",
      actionLabel: "VAT status required",
      tone: "incomplete",
    });
  }

  if (client.countryCode === "XK") {
    if (client.serviceCategory === "property-rent") {
      return suggestion({
        id: "xk-domestic-property-review",
        title: "Property and rent need a specific review",
        summary: "VAT and rent withholding are different treatments. Add only the invoice-visible rule confirmed for this contract; a withholding belongs as a deduction, not as VAT.",
        actionLabel: "Start a custom rule",
        tone: "review",
      });
    }

    if (client.serviceCategory === "other") {
      return suggestion({
        id: "xk-domestic-other-review",
        title: "Choose the applicable Kosovo rate",
        summary: "The standard rate is 18% and a reduced 8% rate exists for defined supplies. This category is too broad to select one safely.",
        actionLabel: "Start a custom rule",
        tone: "review",
      });
    }

    return suggestion({
      id: "xk-domestic-standard",
      title: "Kosovo standard VAT: 18%",
      summary: "For a VAT-registered Kosovo issuer billing a Kosovo client, 18% is the standard starting point. Reduced or exempt supplies still need a category-specific review.",
      lines: [{ id: "vat-standard", label: "VAT", rate: "18", treatment: "added" }],
      actionLabel: "Use 18% VAT default",
      tone: "suggested",
    });
  }

  const generalService = ["professional-services", "digital-services"].includes(client.serviceCategory);
  if (client.customerType === "business" && generalService) {
    return suggestion({
      id: "xk-cross-border-b2b-services",
      title: "Review a no-Kosovo-VAT default",
      summary: "For general B2B services, the place of supply is normally where the taxable business customer is established. Keep Kosovo VAT off only after confirming the customer's taxable status and the service exception rules.",
      invoiceNote: "VAT not charged - place of supply outside Kosovo under Article 20. Customer tax status verified.",
      actionLabel: "Use cross-border default",
      tone: "review",
      evidence: "Keep the client tax ID, contract, and evidence that the recipient is a taxable business.",
    });
  }

  return suggestion({
    id: "xk-cross-border-review",
    title: "Cross-border treatment needs review",
    summary: "Customer type and supply category can change the place of supply. Invoice Studio will not assume that Kosovo VAT is zero for this combination.",
    actionLabel: "Start a custom rule",
    tone: "review",
  });
};

export const profileFromSuggestion = (taxSuggestion) => ({
  useAsDefault: true,
  confirmed: true,
  source: "jurisdiction-suggestion",
  ruleId: taxSuggestion.id,
  lines: cloneTaxLines(taxSuggestion.lines),
  invoiceNote: taxSuggestion.invoiceNote || "",
  explanation: taxSuggestion.summary,
  confirmedAt: new Date().toISOString(),
});

export const noTaxProfile = (explanation = "No invoice-visible tax by default") => ({
  useAsDefault: true,
  confirmed: true,
  source: "manual-no-tax",
  ruleId: "manual-no-tax",
  lines: [],
  invoiceNote: "",
  explanation,
  confirmedAt: new Date().toISOString(),
});

export const customTaxProfile = (profile, lines) => ({
  ...normalizeTaxProfile(profile),
  useAsDefault: true,
  confirmed: false,
  source: "manual",
  ruleId: "manual",
  lines: cloneTaxLines(lines),
  confirmedAt: "",
});

export const taxProfileLabel = (profile) => {
  const normalized = normalizeTaxProfile(profile);
  if (!normalized.confirmed) return "Needs review";
  if (!normalized.lines.length) return "No invoice tax";
  return normalized.lines
    .map((line) => `${line.label || "Tax"} ${Number(line.rate) || 0}% ${line.treatment}`)
    .join(" + ");
};
